import { ApplicationRef, ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, ElementRef, inject, input, OnDestroy, OnInit, Signal, ViewChild} from '@angular/core';
import { MatChipsModule } from '@angular/material/chips';
import { CommonModule } from '@angular/common';
import { EditLogoCarouselComponent } from "../edit-logo-carousel/edit-logo-carousel.component";
import { WebsiteStateService } from '../../../../state-management/services/website-state.service';
import { LogoCarouselImage, WebElement } from '../../../../state-management/model/interfaces/state.model';
import { DEFAULT_LOGO_CAROUSEL_FILENAME } from '../../../../state-management/constants/global.constants';
import { ComponentActions } from '../../../../state-management/model/enums/state.enums';
import { CoordinatesSystem, ScreenWidthBreakpoints } from '../../../../state-management/model/enums/screen-size.enums';
import { ScreensizeStateService } from '../../../../state-management/services/screensize-state.service';
import { SwiperContainer } from 'swiper/element';
import { LoggerService } from '../../../../services/logger.service';
import { Subject, takeUntil } from 'rxjs';
import { S } from '@angular/cdk/keycodes';


export interface SpecifiedLogoCarouselImage extends LogoCarouselImage {
  showInCarousel: boolean;
  isPlaceholder: boolean;
}

export interface LogoCarouselConfig {
  images: string[];
}

const SLIDES_PER_VIEW_DESKTOP = 3;
const SLIDES_PER_VIEW_MOBILE = 1;

@Component({
  selector: 'app-base-logo-carousel',
  templateUrl: './base-logo-carousel.component.html',
  styleUrl: './base-logo-carousel.component.scss',
  imports: [
    MatChipsModule,
    CommonModule,
    EditLogoCarouselComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  // changeDetection: ChangeDetectionStrategy.OnPush //Needs to be disabled in order forthe edit dialog to work/ show up
})
export class BaseLogoCarouselComponent implements OnInit, OnDestroy {
  @ViewChild('logoSwiper') swiperEl!: ElementRef<SwiperContainer>;
  
  readonly elemIds = input.required<string[]>();

  private stateService = inject(WebsiteStateService);
  private elements: Signal<Record<string, WebElement>> = this.stateService.elements;
  private actions = this.stateService.actions;

  public configId!: string;
  public allLogos!: Signal<SpecifiedLogoCarouselImage[]>;

  public logoCarouselFolder = this.stateService.getLogoCarouselStoragePath();
  public logoCarouselFileName = DEFAULT_LOGO_CAROUSEL_FILENAME;

  public isSmallScreen$: Signal<boolean> = this.screensizeState.isScreenSizes(CoordinatesSystem.ScreenWidth, [ScreenWidthBreakpoints.XSmall, ScreenWidthBreakpoints.Small]);
  
  public openEditCarousel = false

  private isStableSubject = new Subject<void>();

  constructor(
    private screensizeState: ScreensizeStateService, 
    private logger: LoggerService, 
    private appRef: ApplicationRef) {

    effect(() => {
      if (this.actions().includes(ComponentActions.EditLogoCarousel)) {
        this.stateService.removeAction(ComponentActions.EditLogoCarousel);
        this.openEditCarousel = true;
        console.log('BaseLogoCarouselComponent Effekt - openEditCarousel gesetzt auf true');
      }
    });

    this.appRef.isStable.pipe(takeUntil(this.isStableSubject)).subscribe((stable) => {
      this.logger.log('App ist stabil: ', stable);
      if (stable && this.carouselLogos().length > 0) {
        this.logger.log('✅ App ist jetzt stabil!');
        this.updateSwiperConfiguration();
        this.isStableSubject.next();
        this.isStableSubject.complete();
      }
    });
  }

  ngOnInit() {
    this.configId = this.getCarouselConfigId()
    this.allLogos = this.stateService.getLogoCarouselById(this.configId);
  }

  ngOnDestroy(): void {
    this.isStableSubject.next();
    this.isStableSubject.complete();
  }

  private updateSwiperConfiguration(): void {
    if (!this.swiperEl || !this.swiperEl.nativeElement.swiper) {
      this.logger.log('❌ Swiper nicht verfügbar für Konfiguration');
      return;
    }

    const swiper = this.swiperEl.nativeElement.swiper;
    const isSmall = this.isSmallScreen$();
    const logos = this.carouselLogos();
    
    // Bestimme Parameter basierend auf Bildschirmgröße
    const slidesPerView = isSmall ? SLIDES_PER_VIEW_MOBILE : SLIDES_PER_VIEW_DESKTOP;
    const shouldEnableLoop = logos.length > slidesPerView;
    const shouldEnableAutoplay = logos.length > slidesPerView;
    const enablePagination = logos.length > slidesPerView;

    // Konfiguriere Swiper Parameter
    swiper.params.loop = shouldEnableLoop;
    swiper.params.pagination = enablePagination ? { clickable: true } : false;
    swiper.params.spaceBetween = 20;
    swiper.params.updateOnWindowResize = true;
    swiper.params.slidesPerView = Math.min(SLIDES_PER_VIEW_MOBILE, logos.length);
    swiper.params.breakpoints = {
      0: {
        slidesPerView: Math.min(SLIDES_PER_VIEW_MOBILE, logos.length),
      },
      600: {
        slidesPerView: Math.min(SLIDES_PER_VIEW_MOBILE, logos.length), // Is needed to recenter slides after flipping mobile devices
      },
      960: {
        slidesPerView: Math.min(SLIDES_PER_VIEW_DESKTOP, logos.length),
      },
    };
    swiper.params.autoplay = shouldEnableAutoplay ? {
      delay: 5000,
      disableOnInteraction: false
    } : false;

    swiper.update();

    if (shouldEnableAutoplay && swiper.autoplay) {
      swiper.autoplay.start();
    } else if (swiper.autoplay) {
      swiper.autoplay.stop();
    }
  }

  public onBreakpointChange(event: any) {
    const [swiper, breakpointParams] = event.detail;
    const slidesPerView = breakpointParams.slidesPerView;
    const logosCount = this.carouselLogos().length;
    const shouldActive = logosCount > slidesPerView;

    if (shouldActive) {
      swiper.autoplay.start();
      swiper.params.loop = true;
    } else {
      swiper.autoplay.stop();
      swiper.params.loop = false;
    }
    swiper.update();
    swiper.slideTo(0, 300); //manually activate autoplay after breakpoint change
  }

  public carouselLogos = computed(() => {
    const logos = this.allLogos()?.filter((logo: SpecifiedLogoCarouselImage) => logo.showInCarousel);
    return logos || [];
  });
  

  private getCarouselConfigId(): string {
    const carouselElement = this.elemIds()
      .map(id => this.elements()[id])
      .find(element => element && element.label === 'carousel');
    return carouselElement ? carouselElement.id : '';
  }
}